<template>
	<div
		id="reception"
		class="w-full"
	>
		<GridLayout
			right-title="ATENDIMENTO POR SENHA"
			:reactive-sidebar="false"
			:sidebar-state="selectedFilters.showSidebar"
		>
			<template #left-content>
				<Filters
					ref="filters"
					:show-sidebar="selectedFilters.showSidebar"
					@change="FiltersChangeHandler"
					@hasAtLeastOneFilter="hasAtLeastOneFilter = $event"
				/>
			</template>
			<template #left-footer>
				<div class="w-full h-full flex items-center justify-center gap-3">
					<button
						class="btn btn-smaller"
						@click="ClearFilters"
					>
						LIMPAR
					</button>
					<button
						class="btn btn-smaller"
						@click="OpenSaveProfilesModal"
					>
						SALVAR
					</button>
					<button
						class="btn btn-smaller"
						@click="ApplyFilters"
					>
						APLICAR
					</button>
				</div>
			</template>
			<template #right-tool-bar>
				<ToolBar>
					<div class="flex items-center gap-3">
						<button
							class="btn btn-tool"
							:class="{
								'has-filters': hasAtLeastOneFilter,
							}"
							@click="HandleToggleSideBar"
						>
							<i
								class="fa-filter mx-2 font-20"
								:class="hasAtLeastOneFilter ? 'fas' : 'far'"
							></i>
						</button>
						<button
							class="btn btn-tool"
							:disabled="isAttending"
							@click="Refresh"
						>
							<i class="fal fa-sync mx-2 font-20"></i>
						</button>
						<button
							class="btn btn-reverse"
							:disabled="isAttending"
							@click="ShowReceptionCounterModal"
						>
							GUICHÊ
							{{ selectedReceptionCounter > 0 ? selectedReceptionCounter : '' }}
						</button>
					</div>
					<div
						id="tab-list"
						class="h-full flex-1 justify-center"
					>
						<a
							class="tab-item"
							:class="{
								active: selectedTab === 'attendings',
							}"
							:disabled="isAttending"
							@click="SelectTab('attendings')"
							>ATENDIMENTO
						</a>
						<a
							class="tab-item"
							:class="{
								active: selectedTab === 'pending',
							}"
							:disabled="isAttending"
							@click="SelectTab('pending')"
							>PENDÊNCIA
						</a>
						<a
							class="tab-item"
							:class="{
								active: selectedTab === 'not_responded',
							}"
							:disabled="isAttending"
							@click="SelectTab('not_responded')"
							>NÃO RESPONDIDO
						</a>
						<a
							class="tab-item"
							:class="{
								active: selectedTab === 'queue',
							}"
							:disabled="isAttending"
							@click="SelectTab('queue')"
							>FILA
						</a>
						<a
							class="tab-item"
							:class="{
								active: selectedTab === 'attended',
							}"
							:disabled="isAttending"
							@click="SelectTab('attended')"
							>ATENDIDOS
						</a>
					</div>
					<div class="flex items-center">
						<button
							class="btn"
							:disabled="isAttending"
							@click="CallNextTicket"
						>
							PRÓXIMO
						</button>
					</div>
				</ToolBar>
			</template>
			<template #right-content>
				<Attending
					v-if="isAttending"
					:selected-attending="selectedAttending"
					@open-flow="selectAtLeastOneFlow = true"
				/>
				<template v-else>
					<Statistics
						v-if="selectedTab === 'attendings'"
						ref="statistics"
					/>
					<NewDatatable
						v-else
						v-model="attendances"
						:headers="headers"
						:selecteds="selectedAttendances"
						:row-colors="{
							emergency: 'var(--emergencyBackgroundColor)',
							priority: 'var(--priorityBackgroundColor)',
							conventional: 'var(--conventionalColor)',
						}"
						row-type-key="category"
						empty-text="NÃO HÁ SENHAS PARA EXIBIR"
						:lazy-loading="true"
						@update:selecteds="selectedAttendances = $event"
						@dblclick="OpenAttendance"
						@load-table="UpdateTable"
					>
						<template #row="{ item }">
							<div class="datatable-cell date">
								<b>
									{{ item.date }}
								</b>
							</div>
							<div class="datatable-cell time">
								<b>
									{{ item.time }}
								</b>
							</div>
							<div class="datatable-cell name">
								<template v-if="item.patient.unknown"> DESCONHECIDO </template>
								<template v-else>
									<b>
										{{ item.patient.name.toUpperCase() }}
									</b>
								</template>
							</div>
							<div class="datatable-cell doc_id">
								<template v-if="item.patient.unknown"> DESCONHECIDO </template>
								<template v-else>
									{{ item.patient.doc_id.tipo }}
									<b>
										{{ item.patient.doc_id.numero }}
									</b>
								</template>
							</div>
							<div class="datatable-cell attendance-type">
								<b>
									{{ item.type.label }}
								</b>
							</div>
							<div class="datatable-cell ticket">
								<span
									class="category-badge"
									:class="item.category"
								>
									<b v-html="item.ticket.toUpperCase()"></b>
								</span>
							</div>
						</template>
					</NewDatatable>
				</template>
			</template>
			<template #right-footer>
				<div
					v-if="!isAttending && selectedTab !== 'attendings'"
					class="w-full h-full"
				>
					<button
						class="btn btn-danger btn-fixed-size"
						:disabled="!hasSelectedAttendances"
						@click="CancelAlert(selectedAttendances)"
					>
						CANCELAR
					</button>
				</div>
				<div
					v-if="isAttending"
					id="action-bar-container"
					class=""
				>
					<div
						id="action-bar-first-group"
						class=""
					>
						<button
							class="btn btn-danger btn-fixed-size"
							:disabled="isAttendingFinished"
							@click="CancelAlert([selectedAttending.id])"
						>
							CANCELAR
						</button>
						<button
							class="btn btn-no-response btn-fixed-size"
							:disabled="shouldDisableNotResponded"
							@click="NotReponded"
						>
							NÃO RESPONDIDO
						</button>
						<button
							class="btn btn-pending btn-fixed-size"
							:disabled="shouldDisablePending"
							@click="Pending"
						>
							PENDENTE
						</button>
					</div>
					<div
						id="action-bar-second-group"
						class=""
					>
						<button
							class="btn btn-fixed-size"
							@click="ReturnToQueue"
						>
							DEVOLVER
						</button>
					</div>
					<div
						id="action-bar-third-group"
						class=""
					>
						<button
							class="btn btn-success btn-fixed-size"
							:disabled="!selectAtLeastOneFlow"
							@click="FinishAttending"
						>
							FINALIZAR
						</button>
					</div>
				</div>
			</template>
		</GridLayout>

		<ReceptionCounterModal
			v-if="showReceptionCounterModal"
			ref="receptionCounterModal"
			@close="showReceptionCounterModal = false"
		/>
	</div>
</template>

<script>
	import Swal from 'sweetalert2'
	import { mapActions, mapGetters } from 'vuex'
	import {
		useConfirmation,
		useCopyObject,
		useDeleteAlert,
		useEachRecursively,
		useError,
		useOk,
		useWarn,
	} from '@/composables'
	import Filters from './components/Filters.vue'
	import Statistics from './components/statistics/Statistics.vue'
	import Attending from './components/Attending.vue'
	import ReceptionCounterModal from './components/ReceptionCounterModal.vue'

	const STATUS_ATTENDED_RECEPTION = 31

	export default {
		name: 'Reception',
		components: {
			Filters,
			ReceptionCounterModal,
			Statistics,
			Attending,
		},
		data() {
			return {
				selectedTab: 'attendings',
				selectedAttending: null,
				selectedAttendances: [],
				selectAtLeastOneFlow: false,
				headers: [
					{ value: 'date', text: 'DATA', width: '7rem' },
					{ value: 'time', text: 'HORA', width: '5rem' },
					{ value: 'patient.name', text: 'NOME' },
					{
						value: 'patient.doc_id',
						text: 'ID',
						width: '11rem',
					},
					{
						value: 'type.label',
						text: 'ATENDIMENTO',
						width: '18rem',
					},
					{ value: 'ticket', text: 'SENHA', width: '9rem' },
				],
				attendances: [],
				hasAtLeastOneFilter: false,
				selectedFilters: {
					showSidebar: true,
					templates: {},
					attendanceTypes: {},
					priorities: {},
				},
				appliedFilters: {
					showSidebar: true,
					templates: {},
					attendanceTypes: {},
					priorities: {},
				},
				showReceptionCounterModal: false,
				intervalRefresh: null,
				limit: 200,
				offset: 0,
				order: 'asc',
				total: 0,
			}
		},
		computed: {
			...mapGetters('auth', ['requester_selected_id']),
			...mapGetters('system', ['selectedReceptionCounter']),
			isAttending() {
				return !!this.selectedAttending?.id
			},
			isAttendingFinished() {
				return this.selectedAttending?.status === STATUS_ATTENDED_RECEPTION
			},
			hasSelectedAttendances() {
				return this.selectedAttendances.length > 0
			},
			vision() {
				if (['attendings', 'queue'].includes(this.selectedTab)) return null

				return this.selectedTab.toLowerCase()
			},
			shouldDisableNotResponded() {
				if (['pending', 'attended'].includes(this.selectedTab)) return true

				return this.selectAtLeastOneFlow || this.isAttendingFinished
			},
			shouldDisablePending() {
				return this.isAttendingFinished
			},
		},
		watch: {
			selectedAttending: {
				handler(val) {
					this.selectAtLeastOneFlow = false
				},
				deep: true,
				immediate: true,
			},
			selectAtLeastOneFlow: {
				handler(val) {
					if (val) {
						this.UpdateAttendance('RECEPTION', false, false)
					}
				},
				immediate: true,
			},
			selectedReceptionCounter: {
				handler(val) {
					if (val === 0) {
						this.showReceptionCounterModal = true
					}
				},
				immediate: true,
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading']),
			ClearFilters() {
				this.$refs.filters.ClearFilters()
			},
			OpenSaveProfilesModal() {
				this.$refs.filters.OpenSaveProfilesModal()
			},
			ApplyFilters() {
				this.appliedFilters = useCopyObject(this.selectedFilters)

				this.GetAttendances()
			},
			FiltersChangeHandler({ filters, initializing }) {
				if (initializing) {
					this.selectedFilters = useCopyObject(filters)
					this.appliedFilters = useCopyObject(filters)
				} else {
					this.selectedFilters = useCopyObject(filters)
				}
			},
			HandleToggleSideBar() {
				this.selectedFilters.showSidebar = !this.selectedFilters.showSidebar
			},
			async GetAttendances(concat = false) {
				this.StartLoading()

				this.intervalRefresh && clearInterval(this.intervalRefresh)

				this.offset = !concat ? 0 : this.offset

				const { templates, priorities, attendanceTypes } = this.GetFiltersForApi()

				return window.api
					.call('post', '/reception/get-attendances', {
						organization_id: this.requester_selected_id,
						templates,
						categories: priorities,
						attendanceTypes,
						vision: this.vision,
						limit: this.limit,
						offset: this.offset,
						order: this.order,
					})
					.then(({ status, data }) => {
						if (status !== 200) {
							useError('ERRO AO BUSCAR ATENDIMENTOS!')
							return
						}

						if (!concat) this.attendances = []

						this.total = data.total

						const parsedData = data.attendances.map(attendance => {
							return {
								...attendance,
								category: this.ParseCategory(attendance.category),
							}
						})
						this.attendances = [...this.attendances, ...parsedData]
					})
					.finally(() => {
						this.StartIntervalRefresh()
						this.FinishLoading()
					})
			},
			async UpdateTable() {
				console.log('UpdateTable()')
				const Toast = Swal.mixin({
					title: 'NÃO HÁ MAIS SENHAS A SEREM CARREGADAS',
					toast: true,
					position: 'top',
					icon: 'info',
					timer: 4000,
					timerProgressBar: true,
					customClass: {
						container: 'themed',
					},
				})

				if (this.attendances.length >= this.total) {
					Toast.fire()
					return
				}

				this.offset += this.limit

				await this.GetAttendances(true)
			},
			GetFiltersForApi() {
				const selectedTemplates = []
				const selectedPriorities = []
				const selectedAttendanceTypes = []

				useEachRecursively(this.appliedFilters.templates?.children, child => {
					if (child.children?.length > 0) return
					if (child.checked) selectedTemplates.push(child.id)
				})
				useEachRecursively(this.appliedFilters.priorities?.children, child => {
					if (child.children?.length > 0) return
					if (child.checked) selectedPriorities.push(child.value.toUpperCase())
				})
				useEachRecursively(this.appliedFilters.attendanceTypes?.children, child => {
					if (child.children?.length > 0) return
					if (child.checked) selectedAttendanceTypes.push(child.value)
				})

				return {
					templates: selectedTemplates,
					priorities: selectedPriorities,
					attendanceTypes: selectedAttendanceTypes,
				}
			},
			Refresh() {
				if (this.selectedTab === 'attendings') {
					this.$refs.statistics.GetStatistics()
				} else {
					this.GetAttendances()
				}
			},
			ShowReceptionCounterModal() {
				this.showReceptionCounterModal = true
			},
			SelectTab(tab) {
				if (this.isAttending) return
				this.selectedTab = tab

				if (['not_responded', 'attended'].includes(tab)) {
					this.order = 'desc'
				} else {
					this.order = 'asc'
				}

				this.GetAttendances()
			},
			CallNextTicket() {
				const { templates, priorities, attendanceTypes } = this.GetFiltersForApi()

				return window.api
					.call('post', '/reception/call-next-ticket', {
						organization_id: this.requester_selected_id,
						counter_number: this.selectedReceptionCounter,
						templates,
						categories: priorities,
						attendanceTypes,
					})
					.then(({ status, data }) => {
						if (!data.attendance) {
							useOk('NÃO HÁ SENHAS PARA EXIBIR!', 'warning')
							return
						}

						if (status !== 200) {
							useError('ERRO AO CHAMAR PRÓXIMA SENHA!')
							return
						}

						this.selectedAttending = {
							...data.attendance,
							category: this.ParseCategory(data.attendance.category),
						}
					})
			},
			async OpenAttendance(attending) {
				return window.api
					.call('post', '/reception/open-attendance', {
						organization_id: this.requester_selected_id,
						queue_attendance_id: attending.id,
						counter_number: this.selectedReceptionCounter,
					})
					.then(({ status }) => {
						if (status === 400) {
							useOk('A SENHA JÁ FOI CHAMADA', 'warning')
							return
						}

						if (status !== 200) {
							useError('ERRO AO ABRIR O ATENDIMENTO!')
							return
						}

						this.selectedAttending = { ...attending }
					})
			},
			async CancelAlert(queueAttendanceIds) {
				const { isConfirmed } = await useDeleteAlert({
					title: 'DESEJA MESMO DELETAR ESTA(S) SENHA(S)?',
				})

				if (isConfirmed) {
					await this.CancelAttendances(queueAttendanceIds)

					if (this.isAttending) {
						this.selectedAttending = null
					}
				}
			},
			async CancelAttendances(queueAttendanceIds) {
				return window.api
					.call('post', '/reception/cancel-attendances', {
						organization_id: this.requester_selected_id,
						queue_attendance_ids: queueAttendanceIds,
					})
					.then(({ status }) => {
						if (status !== 200) {
							useError('ERRO AO CANCELAR O(S) ATENDIMENTO(S)!')
							return
						}

						useConfirmation()
						this.GetAttendances()
					})
			},
			async NotReponded() {
				await this.UpdateAttendance('NOT_RESPONDED')
				this.selectedAttending = null
			},
			async Pending() {
				await this.UpdateAttendance('PENDING')
				this.selectedAttending = null
			},
			async UpdateAttendance(attendanceStatus, withConfirmation = true, withRefresh = true) {
				return window.api
					.call('post', '/reception/update-attendance', {
						organization_id: this.requester_selected_id,
						queue_attendance_id: this.selectedAttending.id,
						status: attendanceStatus,
					})
					.then(({ status }) => {
						if (status !== 200) {
							useError('ERRO AO ATUALIZAR O ATENDIMENTO!')
							return
						}

						if (withConfirmation) {
							useConfirmation()
						}
						if (withRefresh) {
							this.GetAttendances()
						}
					})
			},
			async ReturnToQueue() {
				this.StartLoading()

				return window.api
					.call('post', '/reception/return-to-queue', {
						organization_id: this.requester_selected_id,
						queue_attendance_id: this.selectedAttending.id,
					})
					.then(({ status }) => {
						if (status === 400) {
							useError('ATENDIMENTO JÁ FINALIZADO!')
							this.selectedAttending = null
							this.GetAttendances()
							return
						}

						if (status !== 200) {
							useError('ERRO AO RETORNAR À FILA!')
							return
						}

						useConfirmation()
						this.selectedAttending = null
						this.GetAttendances()
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			async FinishAttending() {
				return window.api
					.call('post', 'reception/finish-attendance', {
						organization_id: this.requester_selected_id,
						queue_attendance_id: this.selectedAttending.id,
					})
					.then(({ status }) => {
						if (status === 400) {
							useError('ATENDIMENTO JÁ FINALIZADO!')
							this.selectedAttending = null
							this.GetAttendances()
							return
						}

						if (status !== 200) {
							useError('ERRO AO FINALIZAR O ATENDIMENTO!')
							return
						}

						useConfirmation()
						this.selectedAttending = null
						this.GetAttendances()
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			ParseCategory(category) {
				if (category.includes('PRIORITY')) return 'priority'

				return category.toLowerCase()
			},
			StartIntervalRefresh() {
				this.intervalRefresh = setInterval(() => {
					if (this.isAttending) return

					if (this.selectedTab === 'attendings') {
						this.$refs.statistics.GetStatistics()
					} else {
						this.GetAttendances()
					}
				}, 1000 * 60)
			},
		},
		mounted() {
			this.StartIntervalRefresh()
		},
	}
</script>

<style lang="scss" scoped>
	:deep(.btn) {
		height: 2.625rem;
		padding: 0 2.5rem;
		font-size: 0.813rem;
		font-weight: 900;
		line-height: 1.219rem;
		font-size-adjust: none;
		font-stretch: 100%;
		letter-spacing: 0.1em;
	}

	:deep(.btn.btn-smaller) {
		padding: 0 1rem;
	}

	.category-badge {
		font-family: 'Inter', sans-serif;
		font-size: 1.1rem;
		font-weight: 900;

		&.emergency {
			color: var(--emergencyBackgroundColor);
		}
		&.priority {
			color: var(--priorityBackgroundColor);
		}
		&.conventional {
			color: var(--conventionalColor);
		}
	}

	#action-bar-container {
		width: 100%;
		height: 100%;
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		grid-template-areas: 'first_group second_group third_group';
		gap: 1rem;

		#action-bar-first-group {
			grid-area: first_group;
			height: 100%;
			display: flex;
			gap: 0.5rem;
		}

		#action-bar-second-group {
			grid-area: second_group;
			height: 100%;
			display: flex;
			justify-content: center;
			gap: 0.5rem;
		}

		#action-bar-third-group {
			grid-area: third_group;
			height: 100%;
			display: flex;
			justify-content: flex-end;
			gap: 0.5rem;
		}
	}

	#right-footer {
		.btn {
			&.btn-fixed-size {
				width: 11rem;
			}
		}
	}

	:deep(.slashed-zero-custom) {
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		line-height: inherit;
		vertical-align: baseline;
		overflow: hidden;
		font-size: inherit;
	}

	:deep(.slashed-zero-custom)::after {
		content: '';
		position: absolute;
		left: 54%;
		top: 50%;
		width: 0.13em;
		height: 1em;
		background-color: currentColor;
		border-radius: 999px;
		transform: translate(-50%, -50%) rotate(28deg);
		pointer-events: none;
	}

	:deep(.new-datatable) {
		min-height: 42.6rem;
		max-height: 42.6rem;
	}
</style>
